/**
 * Primitives d'interface — volontairement peu nombreuses.
 * Le brief annonce une refonte de DA : mieux vaut cinq composants clairs à
 * remplacer qu'un design system à démonter.
 */

import type { ReactNode } from "react";

export function Button({
  children,
  onClick,
  variant = "default",
  disabled,
  type = "button",
  full,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "default" | "primary" | "ghost" | "danger";
  disabled?: boolean;
  type?: "button" | "submit";
  full?: boolean;
}) {
  const variants = {
    default: "bg-[var(--color-surface-2)] text-[var(--color-text)] hover:bg-[#282828]",
    primary: "bg-[var(--color-accent)] text-white hover:brightness-110",
    ghost: "bg-transparent text-[var(--color-muted)] hover:text-[var(--color-text)]",
    danger: "bg-transparent text-[var(--color-bad)] hover:bg-[#2a1a1a]",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-4 py-2.5 text-sm font-medium transition disabled:opacity-40 disabled:pointer-events-none ${variants[variant]} ${full ? "w-full" : ""}`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      {hint && <span className="text-xs text-[var(--color-muted)]">{hint}</span>}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-xs text-[var(--color-muted)]">{hint}</span>}
      {children}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
    />
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className="min-h-24 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
    />
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "core" | "discovery" | "good" | "warn" | "bad" }) {
  const tones = {
    neutral: "bg-[var(--color-surface-2)] text-[var(--color-muted)]",
    core: "bg-[#1c2740] text-[var(--color-accent)]",
    discovery: "bg-[#2a2410] text-[var(--color-warn)]",
    good: "bg-[#12301f] text-[var(--color-good)]",
    warn: "bg-[#2a2410] text-[var(--color-warn)]",
    bad: "bg-[#2f1618] text-[var(--color-bad)]",
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="max-w-xs text-xs text-[var(--color-muted)]">{hint}</p>}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-line)] border-t-[var(--color-accent)]" />
    </div>
  );
}

/** Jauge de budget — visible en permanence, comme demandé au brief §1. */
export function BudgetBar({ ratio, exhausted }: { ratio: number; exhausted: boolean }) {
  const color = exhausted
    ? "var(--color-bad)"
    : ratio > 0.8
      ? "var(--color-warn)"
      : "var(--color-good)";

  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-[var(--color-line)]">
      <div
        className="h-full transition-all"
        style={{ width: `${Math.min(100, ratio * 100)}%`, background: color }}
      />
    </div>
  );
}
